export interface UploadAuthorization {
  readonly method: 'PUT';
  readonly url: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly expiresAt: string;
}

export interface ObjectStorageObjectStat {
  readonly sizeBytes: number;
  readonly etag: string | null;
}

export interface ObjectStoragePort {
  readonly createUploadAuthorization: (input: {
    readonly objectKey: string;
    readonly mimeType: string;
    readonly sizeBytes: number;
  }) => Promise<UploadAuthorization>;
  readonly uploadObject: (input: {
    readonly upload: UploadAuthorization;
    readonly file: Blob;
  }) => Promise<ObjectStorageObjectStat>;
  readonly createReadAuthorization: (input: { readonly objectKey: string }) => Promise<{
    readonly url: string;
    readonly expiresAt: string;
  }>;
  readonly readTextObject: (input: { readonly objectKey: string }) => Promise<string>;
  readonly statObject: (input: { readonly objectKey: string }) => Promise<ObjectStorageObjectStat | null>;
  readonly deleteObject: (input: { readonly objectKey: string }) => Promise<void>;
}

const LOCAL_OBJECT_EXPIRES_AT = '9999-12-31T23:59:59.999Z';

interface LocalUploadAuthorizationRecord {
  readonly objectKey: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
}

export function createLocalObjectStoragePort(): ObjectStoragePort {
  const objectBlobs = new Map<string, Blob>();
  const objectUrls = new Map<string, string>();
  const objectStats = new Map<string, ObjectStorageObjectStat>();
  const uploadAuthorizations = new Map<string, LocalUploadAuthorizationRecord>();

  return {
    createUploadAuthorization: async ({ objectKey, mimeType, sizeBytes }) => {
      if (mimeType.trim() === '') {
        throw new Error('Object MIME type is required');
      }

      const uploadUrl = toLocalObjectUrl(objectKey);

      objectStats.set(objectKey, {
        sizeBytes,
        etag: null
      });
      uploadAuthorizations.set(uploadUrl, {
        objectKey,
        mimeType,
        sizeBytes
      });

      return {
        method: 'PUT',
        url: uploadUrl,
        headers: {
          'content-type': mimeType
        },
        expiresAt: LOCAL_OBJECT_EXPIRES_AT
      };
    },
    uploadObject: async ({ upload, file }) => {
      if (upload.method !== 'PUT' || !upload.url.startsWith('local-object://')) {
        throw new Error('Local object upload authorization is invalid');
      }

      const authorization = uploadAuthorizations.get(upload.url);

      if (!authorization) {
        throw new Error('Local object upload authorization is invalid');
      }

      if (file.size !== authorization.sizeBytes) {
        throw new Error('Local object upload size does not match authorization');
      }

      // Browser-created Blob instances may not carry a MIME type. Accept empty types and validate when present.
      if (file.type !== '' && file.type !== authorization.mimeType) {
        throw new Error('Local object upload type does not match authorization');
      }

      const stat = {
        sizeBytes: file.size,
        etag: null
      };

      revokeObjectUrl(authorization.objectKey, objectUrls);
      objectBlobs.set(authorization.objectKey, file);
      objectStats.set(authorization.objectKey, stat);

      return stat;
    },
    createReadAuthorization: async ({ objectKey }) => ({
      url: toReadableObjectUrl(objectKey, objectBlobs.get(objectKey), objectUrls),
      expiresAt: LOCAL_OBJECT_EXPIRES_AT
    }),
    readTextObject: async ({ objectKey }) => {
      const objectBlob = objectBlobs.get(objectKey);

      if (!objectBlob) {
        throw new Error('Object content is missing');
      }

      return await objectBlob.text();
    },
    statObject: async ({ objectKey }) => objectStats.get(objectKey) ?? null,
    deleteObject: async ({ objectKey }) => {
      revokeObjectUrl(objectKey, objectUrls);
      objectBlobs.delete(objectKey);
      objectStats.delete(objectKey);
    }
  };
}

function toLocalObjectUrl(objectKey: string): string {
  return `local-object://${objectKey}`;
}

function toReadableObjectUrl(objectKey: string, objectBlob: Blob | undefined, objectUrls: Map<string, string>): string {
  if (!objectBlob) {
    return toLocalObjectUrl(objectKey);
  }

  const existingUrl = objectUrls.get(objectKey);

  if (existingUrl) {
    return existingUrl;
  }

  if (typeof URL.createObjectURL === 'function') {
    const objectUrl = URL.createObjectURL(objectBlob);
    objectUrls.set(objectKey, objectUrl);
    return objectUrl;
  }

  const objectUrl = `blob:${toLocalObjectUrl(objectKey)}`;
  objectUrls.set(objectKey, objectUrl);
  return objectUrl;
}

function revokeObjectUrl(objectKey: string, objectUrls: Map<string, string>): void {
  const objectUrl = objectUrls.get(objectKey);

  if (!objectUrl) {
    return;
  }

  objectUrls.delete(objectKey);

  if (typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(objectUrl);
  }
}
